
import React, { useState, useContext, useEffect } from 'react'
import {
	Button,
	Card,
	CardBody,
	Col,
	Form,
	FormGroup,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'

import { trigger } from '../../events/customEvents'
import UsersService from '../../services/UsersService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'

const SelectExternalUserModalRedux = ({ showExternalUserModal = false, exclusionList = [], activeTierMembers = [], tierId = 0 }) =>
{
	const [modalIsBusy, setModalIsBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [externalUsers, setExternalUsers] = useState([])
	const { t } = useTranslation()
	const [addingNew, setAddingNew] = useState(false)
	const [newUser, setNewUser] = useState({ firstName: { value: '', valid: false }, lastName: { value: '', valid: false }, emailAddress: { value: '', valid: false }, valid: false })
	const [newUserSubmitted, setNewUserSubmitted] = useState(false)
	
	const retrieveFilteredExternalUsers = () =>
	{
		setModalIsBusy(true)
		UsersService
			.getFilteredExternalUsers(exclusionList, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setModalIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setExternalUsers(result.data.actionResult)
			})
			.catch((error) =>
			{
				setModalIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleOnOpened = () =>
	{
		retrieveFilteredExternalUsers()
	}
	
	const handleCheckboxChange = (selected, i) =>
	{
		let externalUser = externalUsers[i]
		externalUser.selected = !selected
		let externalUsersClone = [...externalUsers]
		externalUsersClone[i] = externalUser
		setExternalUsers([...externalUsersClone])
	}
	
	const handleSelectAll = () =>
	{
		let externalUsersClone = [...externalUsers]
		externalUsersClone.forEach((externalUser) => { externalUser.selected = true })
		setExternalUsers([...externalUsersClone])
	}
	
	const handleUnselectAll = () =>
	{
		let externalUsersClone = [...externalUsers]
		externalUsersClone.forEach((externalUser) => { externalUser.selected = false })
		setExternalUsers([...externalUsersClone])
	}
	
	const handleCreateNew = () =>
	{
		setExternalUsers([])
		setNewUser({ firstName: { value: '', valid: false }, lastName: { value: '', valid: false }, emailAddress: { value: '', valid: false }, valid: false })
		setAddingNew(true)
	}
	
	const handleNewUserPropertyChange = (e) =>
	{
		const { name, value} = e.target
		setNewUser((previousNewUser) =>
		{
			const currentNewUser = { ...previousNewUser }
			currentNewUser[name].value = value
			switch (name)
			{
				case 'firstName':
				case 'lastName':
					currentNewUser[name].valid = (value.length > 0)
					break
				case 'emailAddress':
					currentNewUser[name].valid = (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value))
					break
			}
			return currentNewUser
		})
	}
	
	const handleCancelModal = () =>
	{
		setAddingNew(false)
		trigger('selectExternalUserModalClosed', { tierId: tierId, activeTierMembers: activeTierMembers })
	}
	
	const handleSaveSelectedUsers = () =>
	{
		let activeTierMembersClone = [...activeTierMembers]
		
		const selectedExternalMembers = externalUsers.filter((externalUser) => { return externalUser.selected })
		
		if (selectedExternalMembers.length > 0)
		{
			let nextTierMemberId = 1
			if (activeTierMembersClone.length > 0)
			{
				const adminAuctionTierMember = activeTierMembersClone.reduce((prev, current) => (prev && prev.tierMemberId > current.tierMemberId) ? prev : current)
				nextTierMemberId = adminAuctionTierMember.tierMemberId + 1
			}
			
			selectedExternalMembers.forEach(externalUser =>
			{
				externalUser.tierMemberId = nextTierMemberId
				nextTierMemberId++
			})
			
			activeTierMembersClone = activeTierMembersClone.concat(selectedExternalMembers)
		}
		
		trigger('selectExternalUserModalClosed', { activeTierMembers: activeTierMembersClone, tierId: tierId })
	}
	
	const handleSaveNew = (e) =>
	{
		e.preventDefault()
		
		setNewUserSubmitted(true)
		
		const isValid = newUser.firstName.valid && newUser.lastName.valid && newUser.emailAddress.valid
		
		setNewUser((previousNewUser) =>
		{
			const currentNewUser = { ...previousNewUser }
			currentNewUser.valid = newUser.firstName.valid && newUser.lastName.valid && newUser.emailAddress.valid
			return currentNewUser
		})
		
		if (isValid)
		{
			setModalIsBusy(true)
			UsersService
				.addExternalUser({ firstName: newUser.firstName.value, lastName: newUser.lastName.value, emailAddress: newUser.emailAddress.value }, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setModalIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					setExternalUsers([ result.data.actionResult ])
					setAddingNew(false)
					setNewUserSubmitted(false)
				})
				.catch((error) =>
				{
					setModalIsBusy(false)
					SiteService.displayApiError(error)
				})
		}
	}
	
	return (
		<Modal
			size="md"
			isOpen={ showExternalUserModal }
			onOpened={ handleOnOpened }
		>
			<SpinnerComponent
				loading={ modalIsBusy }
				position="centered"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<ModalHeader toggle={() => handleCancelModal()} charCode="Y">
				External Users
			</ModalHeader>
			<ModalBody>
				<Card >
					<CardBody className="px-lg-5 py-lg-5">
						<Form role="form">
							<Row style={{ paddingBottom: '2px' }}>
								<Col md="12">
									<div className="text-right">
										{externalUsers.length > 0 && !addingNew &&
											<>
												<Button style={{ marginLeft: '5px' }} color="primary" size="sm" type="button" onClick={ () => handleSelectAll() }>
													{ t('Modals.SelectExternalUser.Select_All') }
												</Button>
												<Button style={{ marginLeft: '5px' }} color="primary" size="sm" type="button" onClick={ () => handleUnselectAll() }>
													{ t('Modals.SelectExternalUser.Unselect_All') }
												</Button>
											</>
										}
										{!addingNew &&
											<Button style={{ marginLeft: '5px' }} color="primary" size="sm" type="button" onClick={ () => handleCreateNew() }>
												{ t('Modals.SelectExternalUser.Create_New') }
											</Button>
										}
									</div>
								</Col>
							</Row>
							<Row>
								<Col md="12" style={{ textAlign: 'center' }}>
									{externalUsers.length === 0 && !addingNew &&
										<label><small>{ t('Modals.SelectExternalUser.No_Selectable_Users') }</small></label>
									}
								</Col>
							</Row>
							{externalUsers.length > 0 && !addingNew &&
								<Row>
									<Col md="12">
										<div className="tier-group">
											{
												externalUsers.map((externalUser, i) => (
													<FormGroup key={ i }>
														<input
															id={ i }
															type="checkbox"
															checked={ externalUser.selected }
															onChange={() => handleCheckboxChange(externalUser.selected, i)}
														/>
														<label
															className="form-control-label"
															style={{ marginLeft: '3px' }}
															htmlFor={ i }
														>
															{ externalUser.fullName }
														</label>
													</FormGroup>
												))
											}
										</div>
									</Col>
								</Row>
							}
							{addingNew &&
								<Row>
									<Col md="12">
										<FormGroup>
											<label className="form-control-label" >
												{ t('Modals.SelectExternalUser.First_Name') }
											</label>
											<Input
												className="form-control-sm"
												type="text"
												name="firstName"
												value={ newUser.firstName.value }
												onChange={ handleNewUserPropertyChange }
												valid={ newUserSubmitted && newUser.firstName.valid }
												invalid={ newUserSubmitted && !newUser.firstName.valid  }
											/>
										</FormGroup>
										<FormGroup>
											<label className="form-control-label" >
												{ t('Modals.SelectExternalUser.Last_Name') }
											</label>
											<Input
												className="form-control-sm"
												type="text"
												name="lastName"
												value={ newUser.lastName.value }
												onChange={ handleNewUserPropertyChange }
												valid={ newUserSubmitted && newUser.lastName.valid }
												invalid={ newUserSubmitted && !newUser.lastName.valid  }
											/>
										</FormGroup>
										<FormGroup>
											<label className="form-control-label" >
												{ t('Modals.SelectExternalUser.Email_Address') }
											</label>
											<Input
												className="form-control-sm"
												type="text"
												name="emailAddress"
												value={ newUser.emailAddress.value }
												onChange={ handleNewUserPropertyChange }
												valid={ newUserSubmitted && newUser.emailAddress.valid }
												invalid={ newUserSubmitted && !newUser.emailAddress.valid  }
											/>
										</FormGroup>
									</Col>
								</Row>
							}
							<div className="text-center">
								<Button
									className="my-4"
									color="secondary"
									type="button"
									size="sm"
									onClick={ () => handleCancelModal() }
									style={{ marginRight: '5px' }}
								>
									{ t('Modals.SelectExternalUser.Cancel') }
								</Button>
								{externalUsers.length > 0 &&
									<Button
										className="my-4"
										color="primary"
										type="button"
										size="sm"
										onClick={ () => handleSaveSelectedUsers() }
									>
										{ t('Modals.SelectExternalUser.Save_Selected_Users') }
									</Button>
								}
								{addingNew &&
									<Button
										className="my-4"
										color="primary"
										type="button"
										size="sm"
										onClick={ (e) => handleSaveNew(e) }
									>
										{ t('Modals.SelectExternalUser.Save') }
									</Button>
								}
							</div>
						</Form>
					</CardBody>
				</Card>
			</ModalBody>
			<ModalFooter></ModalFooter>
		</Modal>
	)
}

export default SelectExternalUserModalRedux
